import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import type { ProspectStatus } from "./entryStatus.ts";

/**
 * Write actions on a list's contact entries. Shared by the nested per-company
 * panel and the flat "All contacts" tab so both behave identically.
 *
 * Contact writes change per-company contact counts, so the list detail query is
 * invalidated alongside the contacts query, and the prospects (their drawer
 * lists the company's contacts, the collection's rows count them).
 */
export function useContactEntryMutations(listId: string) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: keys.prospectListContactEntry.byList(listId) });
    queryClient.invalidateQueries({ queryKey: keys.prospectList.detail(listId) });
    queryClient.invalidateQueries({ queryKey: keys.prospect.all });
  };

  const accept = useMutation({
    mutationFn: (entryId: string) => api.acceptProspectContact(listId, entryId),
    onSuccess: invalidate,
  });
  const disqualify = useMutation({
    mutationFn: (entryId: string) => api.disqualifyProspectContact(listId, entryId, null),
    onSuccess: invalidate,
  });
  const reset = useMutation({
    mutationFn: (entryId: string) => api.resetProspectContact(listId, entryId),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (entryId: string) => api.removeProspectContact(listId, entryId),
    onSuccess: invalidate,
  });
  const updateNote = useMutation({
    mutationFn: ({ entryId, note }: { entryId: string; note: string | null }) =>
      api.updateProspectContact(listId, entryId, { note }),
    onSuccess: invalidate,
  });

  const setStatus = (entryId: string, current: ProspectStatus, next: ProspectStatus | null) => {
    if (next === null || next === current) return;
    if (next === "accepted") accept.mutate(entryId);
    else if (next === "pending") reset.mutate(entryId);
    else disqualify.mutate(entryId);
  };

  return { accept, disqualify, reset, remove, updateNote, setStatus };
}

export type ContactEntryMutations = ReturnType<typeof useContactEntryMutations>;
